import { useCallback, useEffect, useRef, useState } from 'react';
import { getAlertaPagoEmpresa } from 'src/services/empresaService';
import { debeMostrarAlertaPago } from 'src/utils/alertaPago/politicaAlertaPago';

const marcaSesionKey = (empresaId) => `sorby_alerta_pago_mostrada_${empresaId}`;

const leerMarcaSesion = (empresaId) => {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(marcaSesionKey(empresaId)) === '1';
  } catch (e) {
    return false;
  }
};

const escribirMarcaSesion = (empresaId) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(marcaSesionKey(empresaId), '1');
  } catch (e) {
    /* modo privado / storage bloqueado: el aviso simplemente reaparece */
  }
};

/**
 * Aviso de pago atrasado del dashboard.
 *
 * La config se consulta una sola vez por carga de la app (el layout no se
 * desmonta al navegar en Next), y la decisión de abrir vive en la función pura
 * `debeMostrarAlertaPago`. La marca de "ya se mostró" va en almacenamiento de
 * sesión: se borra al cerrar el navegador, así el aviso vuelve al día siguiente.
 */
export const useAlertaPago = (empresaId, pathname) => {
  const [config, setConfig] = useState(null);
  const [abierto, setAbierto] = useState(false);
  const pathnameAnteriorRef = useRef(null);

  useEffect(() => {
    if (!empresaId) return;
    let cancelado = false;
    getAlertaPagoEmpresa(empresaId).then((data) => {
      if (!cancelado && data) setConfig(data);
    });
    return () => { cancelado = true; };
  }, [empresaId]);

  useEffect(() => {
    if (!config || !empresaId) return;

    const mostrar = debeMostrarAlertaPago({
      modo: config.modo,
      pathname,
      pathnameAnterior: pathnameAnteriorRef.current,
      yaSeMostroEnSesion: leerMarcaSesion(empresaId),
    });
    pathnameAnteriorRef.current = pathname;

    if (mostrar) {
      escribirMarcaSesion(empresaId);
      setAbierto(true);
    }
  }, [config, pathname, empresaId]);

  // Sin esto, apagar el aviso no le llega al cliente que ya tiene el dashboard
  // abierto hasta que recargue — y no tiene cómo saber que debe hacerlo. Volver
  // a la pestaña sí es algo que hace solo. Solo se revalida con el aviso
  // prendido: la mayoría de las empresas lo tiene apagado y no debe pagar
  // consultas por una feature que no usa.
  const modo = config?.modo;
  useEffect(() => {
    if (!empresaId || !modo || modo === 'ninguna') return;

    const revalidar = () => {
      if (document.visibilityState !== 'visible') return;
      getAlertaPagoEmpresa(empresaId).then((data) => {
        if (!data) return;
        setConfig(data);
        if (data.modo === 'ninguna') setAbierto(false);
      });
    };

    document.addEventListener('visibilitychange', revalidar);
    return () => document.removeEventListener('visibilitychange', revalidar);
  }, [empresaId, modo]);

  const cerrar = useCallback(() => setAbierto(false), []);

  return { abierto, mensaje: config?.mensaje || null, cerrar };
};
